import React from 'react';
import {
  Modal as ChakraModal,
  ModalProps as ChakraModalProps,
  ModalCloseButton as ChakraModalCloseButton,
  ModalCloseButtonProps,
  ModalBody,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  useStyleConfig,
  Box,
  forwardRef,
} from '@chakra-ui/react';
import { ColorMode } from '@allergan-data-labs/alle-elements-core';
import { getCloseButtonStyles } from './modal.theme';

export interface ModalProps extends ChakraModalProps {
  /**
   * The color mode for the modal.
   * @default light
   */
  colorMode?: ColorMode;
  /**
   * The size of the modal.
   * @default md
   */
  size?: ModalSizeProps;
  /**
   * If `true`, the close button will be hidden.
   * @default false
   * @warning This prop will be deprecated in the future. Use `ModalCloseButton` instead.
   */
  hideCloseButton?: boolean;
}

export type ModalSizeProps = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';

export const ModalCloseButton = forwardRef<ModalCloseButtonProps, 'button'>(
  ({ ...props }, ref) => {
    const styles = useStyleConfig('Button', {
      variant: 'solid',
      colorScheme: 'brand',
    });

    return <ChakraModalCloseButton ref={ref} _css={styles} {...props} />;
  }
);

/**
 * A modal is a dialog that focuses the user's attention exclusively on an information via a window that is overlaid on primary content.
 */
export const Modal = ({
  colorMode = 'light',
  size = 'md',
  hideCloseButton = false,
  children,
  ...rest
}: ModalProps) => {
  return (
    <ChakraModal
      isCentered
      scrollBehavior="inside"
      colorScheme={colorMode}
      size={size}
      {...rest}
    >
      <Box sx={{ ...getCloseButtonStyles({ hideCloseButton }) }}>
        <div>{children}</div>
      </Box>
    </ChakraModal>
  );
};

export { ModalBody, ModalOverlay, ModalContent, ModalHeader, ModalFooter };
export type { ModalCloseButtonProps };
